import { omit } from "lodash";

import { useNorthbeamMutationPromise } from "@hooks/http";
import {
  type UpsertMMMClientSetting,
  type UpsertMMMClientSettingVariables,
} from "@north-beam/nb-client-side";
import { type MMMClientCycle } from "@north-beam/nb-common";
import { CLIENT_SETTINGS_FOR_CYCLE_QUERY } from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { UPSERT_CLIENT_SETTING } from "../form/upsert-mutation";
import { type ClientSetting } from "../types";

export function useUpsertClientSetting() {
  const { setClientSettings } = useClientSettings();
  const { selectedCycle } = useSelectedCycle();

  const [_upsertClientSetting, { loading, error }] =
    useNorthbeamMutationPromise<
      UpsertMMMClientSetting,
      UpsertMMMClientSettingVariables
    >(UPSERT_CLIENT_SETTING, {
      doNotRaiseErrorOnFailure: true,
      refetchQueries: [
        {
          query: CLIENT_SETTINGS_FOR_CYCLE_QUERY,
          variables: { id: selectedCycle.id },
        },
      ],
    });

  const transformClientSettingToPayload = (
    clientSetting: ClientSetting,
    cycleForSetting: MMMClientCycle,
  ) => {
    const payload = {
      data: {
        ...omit(
          clientSetting,
          "locked",
          "createBudgetRecommendationRunResults",
        ),
        __typename: undefined,
        cycle_id: selectedCycle.id,
        client_id: undefined,
        target: undefined,
        client_setting_date_range: undefined,
        id: undefined,
        activated_at: undefined,
        retired_at: undefined,
        kpi: undefined,
        kpi_v2: undefined,
        start_date: cycleForSetting.date_range.start_date,
        end_date: cycleForSetting.date_range.end_date,
        kpi_id: clientSetting.kpi ? String(clientSetting.kpi.id) : undefined,
        kpi_v2_id: clientSetting.kpi_v2
          ? String(clientSetting.kpi_v2.id)
          : undefined,
        desired_kpi_value: clientSetting.desired_kpi_value
          ? clientSetting.desired_kpi_value
          : undefined,
        // mix_validations: [],
        name: clientSetting.name + " (Copy)",
        budget_high: String(clientSetting.budget_high),
        budget_low: String(clientSetting.budget_low),

        target_spend: String(clientSetting.target_spend),
        profit_value_per_unit_kpi: clientSetting.profit_value_per_unit_kpi
          ? String(clientSetting.profit_value_per_unit_kpi)
          : "",
        max_cost_per_unit_kpi: clientSetting.max_cost_per_unit_kpi
          ? String(clientSetting.max_cost_per_unit_kpi)
          : "",
        spend_constraints: clientSetting.spend_constraints.map((constraint) => {
          return {
            budget_low:
              constraint.budget_low === null
                ? null
                : String(constraint.budget_low),
            budget_high:
              constraint.budget_high === null
                ? null
                : String(constraint.budget_high),
            data_column_id: constraint.feature
              ? String(constraint.feature.id)
              : undefined,
            data_column_v2_id: constraint.feature_v2
              ? String(constraint.feature_v2.id)
              : undefined,
          };
        }),
      },
    };

    return payload;
  };

  const upsertClientSetting = async (
    payload: UpsertMMMClientSettingVariables,
  ) => {
    try {
      const { upsertMMMClientSetting: res } = await _upsertClientSetting(
        payload,
      );
      if (error || !res.clientSetting) {
        return false;
      } else {
        setClientSettings((prevSettings: ClientSetting[]) => {
          let filteredAtIndex = -1;
          // NOTE: Nexus enums incompatible w/ TS enums
          const newSetting = res.clientSetting as unknown as ClientSetting;
          const filteredPrevSettings = prevSettings.filter((setting, i) => {
            const shouldKeep = setting.id !== newSetting.id;
            if (!shouldKeep) {
              filteredAtIndex = i;
            }
            return shouldKeep;
          });
          if (filteredAtIndex === -1) {
            return [
              ...filteredPrevSettings,
              {
                ...newSetting,
              },
            ];
          }
          filteredPrevSettings.splice(filteredAtIndex, 0, newSetting);
          return filteredPrevSettings;
        });
        return true;
      }
    } catch (err) {
      return false;
    }
  };

  return {
    upsertClientSetting,
    loading,
    networkError: error?.message,
    transformClientSettingToPayload,
  };
}
